/**
 * Partner photos and logos (LIM-4606): what the form accepts, and the
 * browser-side downscale that runs before anything is uploaded.
 *
 * A phone photo is 12 MB at 4032 px; the signed upload URL admits at most
 * 5 MiB, and the app's cards never draw more than a few hundred pixels. So
 * every image is drawn onto a canvas no larger than its kind's long edge and
 * re-encoded before it leaves the browser. Re-encoding also drops the EXIF
 * block, phone location included.
 */

import { brandColors } from '@liminal/brand/palette';
import type { RewardMerchantImage } from '@/api/types';

export type MerchantImageKind = 'photo' | 'logo';

/** A registered image with the URI it is served from, which is all a thumbnail needs. */
export type ServedImage = RewardMerchantImage & { readonly uri: string };

/** The logo as the merchant names it: one of its images, and where that is served from. */
export interface MerchantLogo {
  readonly name: string;
  readonly uri: string;
}

/** The media types partner-service signs uploads for (`^image/(jpeg|png|webp)$`). */
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type AcceptedImageType = (typeof ACCEPTED_IMAGE_TYPES)[number];

/**
 * The media types an operator may pick: the accepted ones, plus SVG. An SVG
 * is never uploaded as one: it is drawn onto the canvas like any other image
 * and leaves the browser as one of the accepted types, so the bucket, the
 * app and partner-service only ever see a bitmap.
 */
export const SELECTABLE_IMAGE_TYPES = [...ACCEPTED_IMAGE_TYPES, 'image/svg+xml'] as const;
export type SelectableImageType = (typeof SELECTABLE_IMAGE_TYPES)[number];

/** The most bytes a signed upload admits: RewardMerchantImageUpload.size_bytes. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** The most photos a partner has (the plan's limit; partner-service enforces it on update). */
export const MAX_PHOTOS = 10;

export const JPEG_QUALITY = 0.85;

interface ResizeTarget {
  /** The longest edge the image is drawn at, in pixels. Never upscaled. */
  readonly maxEdge: number;
  /** Keep a PNG's transparency rather than flatten it into a JPEG. */
  readonly keepTransparency: boolean;
}

/** Photos fill a card; a logo is a small mark, whose transparency is part of it. */
export const RESIZE_TARGETS: Record<MerchantImageKind, ResizeTarget> = {
  photo: { maxEdge: 2048, keepTransparency: false },
  logo: { maxEdge: 512, keepTransparency: true },
};

export function isSelectableImageType(type: string): type is SelectableImageType {
  return SELECTABLE_IMAGE_TYPES.some((selectable) => selectable === type);
}

/** The merchant and image ids of an image's resource name, `rewardMerchants/{m}/images/{i}`. Throws on any other name. */
export function merchantImageIds(name: string): { readonly merchantId: string; readonly imageId: string } {
  const parts = name.split('/');
  if (parts.length !== 4 || parts[0] !== 'rewardMerchants' || parts[2] !== 'images' || parts[1] === '' || parts[3] === '') {
    throw new Error(`"${name}" is not a partner image resource name`);
  }
  return { merchantId: parts[1], imageId: parts[3] };
}

/** The size an image is drawn at: its own, or scaled down so its longest edge is `maxEdge`. */
export function fitWithin(width: number, height: number, maxEdge: number): { readonly width: number; readonly height: number } {
  if (!(width > 0) || !(height > 0) || !(maxEdge > 0)) {
    throw new Error(`cannot fit a ${width}x${height} image within ${maxEdge}px`);
  }
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

export interface ResizedImage {
  readonly blob: Blob;
  readonly mimeType: AcceptedImageType;
  readonly width: number;
  readonly height: number;
}

/** True when any pixel is not fully opaque. RGBA, so every fourth byte is alpha. */
function hasTransparency(rgba: Uint8ClampedArray): boolean {
  for (let i = 3; i < rgba.length; i += 4) {
    if (rgba[i] < 255) return true;
  }
  return false;
}

function encode(canvas: HTMLCanvasElement, mimeType: AcceptedImageType, quality: number | undefined): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error(`the browser could not encode the image as ${mimeType}`))),
      mimeType,
      quality,
    );
  });
}

/** A decoded image ready to draw, its size, and how to let go of it once drawn. */
interface DecodedImage {
  readonly source: CanvasImageSource;
  readonly width: number;
  readonly height: number;
  readonly release: () => void;
}

/**
 * Decodes an SVG through an <img>: createImageBitmap refuses SVG blobs. An
 * <img> runs none of the file's scripts. The object URL is held until the
 * image has been drawn.
 */
async function decodeSvg(file: File): Promise<DecodedImage> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return { source: image, width: image.naturalWidth, height: image.naturalHeight, release: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

/**
 * Draws the file at most `maxEdge` px on its long edge and re-encodes it: a
 * JPEG at {@link JPEG_QUALITY}, or a PNG for a logo that has transparent
 * pixels. An SVG is a vector, so it is drawn at exactly `maxEdge` rather than
 * only ever scaled down. Rejects when the file cannot be decoded as an image;
 * the caller shows that, it never uploads the original instead.
 */
export async function resizeImage(file: File, kind: MerchantImageKind): Promise<ResizedImage> {
  const target = RESIZE_TARGETS[kind];
  const svg = file.type === 'image/svg+xml';
  let decoded: DecodedImage;
  if (svg) {
    decoded = await decodeSvg(file);
  } else {
    // `from-image` applies the EXIF rotation, so a portrait phone photo stays
    // portrait once the EXIF block is gone.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    decoded = { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
  }
  try {
    // An SVG's own size only sets its proportions: fitted from far larger
    // than the edge, it comes out at exactly the edge.
    const { width, height } = svg
      ? fitWithin(decoded.width * target.maxEdge, decoded.height * target.maxEdge, target.maxEdge)
      : fitWithin(decoded.width, decoded.height, target.maxEdge);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('the browser gave no 2d canvas to scale the image on');
    ctx.drawImage(decoded.source, 0, 0, width, height);

    const keepAlpha =
      target.keepTransparency && file.type !== 'image/jpeg' && hasTransparency(ctx.getImageData(0, 0, width, height).data);
    if (keepAlpha) return { blob: await encode(canvas, 'image/png', undefined), mimeType: 'image/png', width, height };

    // JPEG has no alpha channel: paint white behind the image, so a
    // transparent area turns white rather than black.
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = brandColors.white;
    ctx.fillRect(0, 0, width, height);
    return { blob: await encode(canvas, 'image/jpeg', JPEG_QUALITY), mimeType: 'image/jpeg', width, height };
  } finally {
    decoded.release();
  }
}
